// Shared display maps + form metadata for the Lead Generation staff surfaces.
// Pure data (no React) so it stays out of the stale-after-save gate's way.

import { formatCurrency, formatDateInTz, isoDateInTz } from "@/design-system/lib/format";
import type {
  PreleadCimStatus,
  PreleadInterestLevel,
  PreleadNdaStatus,
  PreleadSource,
  PreleadStatus,
} from "@/api/leadGen/preleads";
import type { LeadGeneratorStatus } from "@/api/leadGen/leadGenerators";
import type { LeadGenEarningState } from "@/api/leadGen/earnings";
import type { LeadGenPlanConfig } from "@/api/leadGen/compensationPlan";

/** The Badge component's `status` tones. */
export type BadgeTone = "draft" | "info" | "gold" | "finalized" | "archived" | "review" | "error";

/** The empty GUID — the program-default scope for the effective-config read. */
export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// --- Prelead ---------------------------------------------------------------

export const PRELEAD_STATUS_LABEL: Record<PreleadStatus, string> = {
  Draft: "Draft",
  MessageSent: "Message sent",
  ResponseReceived: "Response received",
  NdaRequested: "NDA requested",
  NdaSubmitted: "NDA submitted",
  CimRequested: "CIM requested",
  CimReceived: "CIM received",
  Interested: "Interested",
  Escalated: "Escalated",
  Contacted: "Contacted",
  BadFit: "Bad fit",
  Duplicate: "Duplicate",
  NoResponse: "No response",
  Closed: "Closed",
  Disqualified: "Disqualified",
};

export const PRELEAD_STATUS_BADGE: Record<PreleadStatus, BadgeTone> = {
  Draft: "draft",
  MessageSent: "info",
  ResponseReceived: "info",
  NdaRequested: "review",
  NdaSubmitted: "review",
  CimRequested: "review",
  CimReceived: "review",
  Interested: "gold",
  Escalated: "finalized",
  Contacted: "finalized",
  BadFit: "error",
  Duplicate: "archived",
  NoResponse: "archived",
  Closed: "archived",
  Disqualified: "error",
};

/** The production-line order for the funnel/pipeline view. */
export const PRELEAD_PIPELINE: PreleadStatus[] = [
  "Draft",
  "MessageSent",
  "ResponseReceived",
  "NdaRequested",
  "NdaSubmitted",
  "CimRequested",
  "CimReceived",
  "Interested",
  "Escalated",
  "Contacted",
];

export const PRELEAD_STATUSES: PreleadStatus[] = [
  ...PRELEAD_PIPELINE,
  "BadFit",
  "Duplicate",
  "NoResponse",
  "Closed",
  "Disqualified",
];

export const PRELEAD_SOURCE_LABEL: Record<PreleadSource, string> = {
  BizBuySell: "BizBuySell",
  DealStream: "DealStream",
  LoopNet: "LoopNet",
  BrokerWebsite: "Broker website",
  LinkedIn: "LinkedIn",
  ColdOutreach: "Cold outreach",
  Referral: "Referral",
  Marketplace: "Marketplace",
  Other: "Other",
};

export const PRELEAD_SOURCES: PreleadSource[] = [
  "BizBuySell",
  "DealStream",
  "LoopNet",
  "BrokerWebsite",
  "LinkedIn",
  "ColdOutreach",
  "Referral",
  "Marketplace",
  "Other",
];

export const INTEREST_BADGE: Record<PreleadInterestLevel, BadgeTone> = {
  Unknown: "draft",
  Cold: "info",
  Warm: "gold",
  Hot: "error",
};

export const NDA_STATUS_LABEL: Record<PreleadNdaStatus, string> = {
  NotRequested: "Not requested",
  Requested: "Requested",
  Completed: "Completed",
};

export const CIM_STATUS_LABEL: Record<PreleadCimStatus, string> = {
  NotRequested: "Not requested",
  Requested: "Requested",
  Received: "Received",
};

// --- Lead Generator --------------------------------------------------------

export const GENERATOR_STATUS_LABEL: Record<LeadGeneratorStatus, string> = {
  Invited: "Invited",
  Active: "Active",
  Suspended: "Suspended",
  Inactive: "Inactive",
};

export const GENERATOR_STATUS_BADGE: Record<LeadGeneratorStatus, BadgeTone> = {
  Invited: "info",
  Active: "finalized",
  Suspended: "review",
  Inactive: "archived",
};

// --- Earnings --------------------------------------------------------------

export const EARNING_STATE_BADGE: Record<LeadGenEarningState, BadgeTone> = {
  Estimated: "draft",
  PendingReview: "review",
  Approved: "finalized",
  Adjusted: "archived",
};

/** Reward-bucket display labels (LeadGenBucket codes on the wire). */
export const BUCKET_LABEL: Record<string, string> = {
  outreach: "Outreach",
  nda_cim: "NDA / CIM",
  interested_escalations: "Interested escalations",
  appointments: "Appointments",
};

export function bucketLabel(code: string): string {
  return BUCKET_LABEL[code] ?? code;
}

/** KPI display labels (LeadGenKpi codes on the wire). */
export const KPI_LABEL: Record<string, string> = {
  prospect_created: "Prospect created",
  nda_completed: "NDA completed",
  cim_received: "CIM received",
  interested_escalation: "Interested escalation",
  completed_appointment: "Completed appointment",
};

export const KPI_CODES = [
  "prospect_created",
  "nda_completed",
  "cim_received",
  "interested_escalation",
  "completed_appointment",
] as const;

export const GRADES = ["A", "B", "C", "D", "F"] as const;

// --- Compensation plan editor field metadata -------------------------------

export type PlanFieldKind = "dollar" | "count" | "multiplier" | "weight";

export interface PlanField {
  key: keyof LeadGenPlanConfig;
  label: string;
  kind: PlanFieldKind;
  /** Nullable = blank means "unset" (no cap / no limit on that axis). */
  nullable: boolean;
}

export interface PlanFieldGroup {
  title: string;
  help: string;
  fields: PlanField[];
}

const dollar = (key: keyof LeadGenPlanConfig, label: string, nullable = true): PlanField => ({
  key,
  label,
  kind: "dollar",
  nullable,
});

export const PLAN_FIELD_GROUPS: PlanFieldGroup[] = [
  {
    title: "KPI rates",
    help: "Dollars credited per one-click KPI action. Not a rate of pay — a display figure the manager reconciles and pays off-system.",
    fields: [
      dollar("prospectCreatedRate", "Prospect created (first contact)", false),
      dollar("ndaCompletedRate", "NDA completed", false),
      dollar("cimReceivedRate", "CIM received", false),
      dollar("interestedEscalationRate", "Interested escalation", false),
      dollar("completedAppointmentRate", "Completed appointment", false),
    ],
  },
  {
    title: "Weekly bucket caps",
    help: "The most a bucket can credit in a week. Blank = uncapped on that axis.",
    fields: [
      dollar("outreachWeeklyCap", "Outreach"),
      dollar("ndaCimWeeklyCap", "NDA / CIM"),
      dollar("interestedEscalationsWeeklyCap", "Interested escalations"),
      dollar("appointmentsWeeklyCap", "Appointments"),
      dollar("totalWeeklyCap", "Total (all buckets)"),
    ],
  },
  {
    title: "Monthly bucket caps",
    help: "The most a bucket can credit in a rolling 4-week month. Blank = uncapped.",
    fields: [
      dollar("outreachMonthlyCap", "Outreach"),
      dollar("ndaCimMonthlyCap", "NDA / CIM"),
      dollar("interestedEscalationsMonthlyCap", "Interested escalations"),
      dollar("appointmentsMonthlyCap", "Appointments"),
      dollar("totalMonthlyCap", "Total (all buckets)"),
    ],
  },
  {
    title: "Daily bucket caps",
    help: "Optional per-day dollar ceiling per bucket. Blank = no daily dollar cap.",
    fields: [
      dollar("outreachDailyCap", "Outreach"),
      dollar("ndaCimDailyCap", "NDA / CIM"),
      dollar("interestedEscalationsDailyCap", "Interested escalations"),
      dollar("appointmentsDailyCap", "Appointments"),
      dollar("totalDailyCap", "Total (all buckets)"),
    ],
  },
  {
    title: "Daily count limits",
    help: "The runaway guard — only the first N of a KPI per day pay. Blank = unlimited.",
    fields: [
      { key: "prospectCreatedDailyCountLimit", label: "Prospect created", kind: "count", nullable: true },
      { key: "ndaCompletedDailyCountLimit", label: "NDA completed", kind: "count", nullable: true },
      { key: "cimReceivedDailyCountLimit", label: "CIM received", kind: "count", nullable: true },
      { key: "interestedEscalationDailyCountLimit", label: "Interested escalation", kind: "count", nullable: true },
      { key: "completedAppointmentDailyCountLimit", label: "Completed appointment", kind: "count", nullable: true },
    ],
  },
  {
    title: "Quality multiplier bands",
    help: "The factor applied to the week's figure at pay-period approval, by letter grade (1.00 = 100%).",
    fields: [
      { key: "multiplierA", label: "Grade A", kind: "multiplier", nullable: false },
      { key: "multiplierB", label: "Grade B", kind: "multiplier", nullable: false },
      { key: "multiplierC", label: "Grade C", kind: "multiplier", nullable: false },
      { key: "multiplierD", label: "Grade D", kind: "multiplier", nullable: false },
      { key: "multiplierF", label: "Grade F", kind: "multiplier", nullable: false },
    ],
  },
  {
    title: "Grade weights",
    help: "Displayed guide for how a manager weighs the quality grade. Should sum to 100.",
    fields: [
      { key: "gradeWeightContact", label: "Contact", kind: "weight", nullable: false },
      { key: "gradeWeightNdaCim", label: "NDA / CIM", kind: "weight", nullable: false },
      { key: "gradeWeightInterested", label: "Interested", kind: "weight", nullable: false },
      { key: "gradeWeightAppointment", label: "Appointment", kind: "weight", nullable: false },
      { key: "gradeWeightQuality", label: "Quality", kind: "weight", nullable: false },
    ],
  },
];

/**
 * Today's calendar date as `YYYY-MM-DD` (for the week-of picker default).
 *
 * `tz` is optional only because this module has no hook context and its five
 * call sites live in other files; omitting it reads the app's zone rather
 * than the viewer's. Either way it is no longer the UTC day, which from 17:00
 * Phoenix defaulted the week-of picker to TOMORROW. Pass the viewer's `tz`
 * (`useUserTimeZone()`) from a component when you touch a call site.
 */
export function todayInputDate(tz?: string): string {
  return isoDateInTz(new Date(), tz);
}

/** Dollars-and-cents money formatter (earning rates go down to $0.10). Renders
 *  an em-dash for null/unset. */
export function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return formatCurrency(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A readable display name for a prelead. Business name → contact name → first
 *  line of the raw paste → listing URL host → "Prelead — <date>". Empty/partial
 *  captures (web-form outreach with no business/contact) are normal now, so this
 *  keeps the monitor usable without an "Untitled prelead". `rawText` is the
 *  summary's short snippet or, on the detail view, the full raw pasted text.
 *
 *  <p>`createdAt` is an INSTANT — `Prelead` is a `BaseEntity` and neither
 *  creation path assigns `CreatedAt`, so it takes the base default
 *  `= DateTimeOffset.UtcNow` — which is why the date-fallback branch renders
 *  through `formatDateInTz` rather than a bare `toLocaleDateString()`. A bare
 *  call takes BOTH its locale and its zone from the browser, so the same
 *  prelead read differently on two machines and differently from every other
 *  date in the product.</p>
 *
 *  <p>🔴 <b>`tz` IS TRAILING AND OPTIONAL, AND THAT IS A DELIBERATE TRADE, NOT
 *  AN OVERSIGHT — read this before deciding it is finished.</b> This module is
 *  PURE (its own header says so, so that it stays out of the stale-after-save
 *  gate's way) and therefore cannot call `useDateFormatter` itself; the viewer's
 *  zone has to be threaded in from the calling component. Omitted, it degrades
 *  to `FALLBACK_TIMEZONE` (America/Phoenix) — which already removes the
 *  machine-dependence, because the answer is now the same everywhere — but it
 *  does NOT yet honour a viewer whose configured zone is something else.</p>
 *
 *  <p>The parameter is placed AFTER the existing object so no positional call
 *  can rebind onto it. <b>Every staff call site threads it</b> —
 *  `GeneratorDetailPage` and `PreleadsListPage` (twice), each widening its
 *  existing `useDateFormatter()` destructure — so the staff surfaces are
 *  PER-VIEWER, not merely deterministic. The optionality remains for the
 *  fallback's own sake, and a call that omits it still lands on
 *  `FALLBACK_TIMEZONE` rather than on the browser; a test pins that ladder so
 *  the default cannot quietly become the machine's zone again.</p>
 *
 *  <p>Note also the SEPARATE portal copy of this helper at
 *  `src/portal/tabs/leadGen/preleadStatus.ts` — a different module with its own
 *  `toLocaleDateString()` and its own callers. Fixing this one does not reach
 *  it.</p> */
export function preleadDisplayName(
  p: {
    businessName?: string | null;
    contactFirstName?: string | null;
    contactLastName?: string | null;
    rawText?: string | null;
    listingUrl?: string | null;
    createdAt?: string | null;
  },
  tz?: string | null,
): string {
  const business = p.businessName?.trim();
  if (business) return business;
  const contact = [p.contactFirstName, p.contactLastName].filter(Boolean).join(" ").trim();
  if (contact) return contact;
  const line = firstNonEmptyLine(p.rawText);
  if (line) return line.length > 60 ? `${line.slice(0, 60).trimEnd()}…` : line;
  const host = urlHost(p.listingUrl);
  if (host) return host;
  const created = p.createdAt ? new Date(p.createdAt) : null;
  return created && !Number.isNaN(created.getTime())
    ? `Prelead — ${formatDateInTz(created, tz)}`
    : "Prelead";
}

function firstNonEmptyLine(text?: string | null): string | null {
  if (!text) return null;
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function urlHost(url?: string | null): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).hostname || null;
  } catch {
    return null;
  }
}
